import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import CardPreview from './ScannerPage/components/CardPreview';
import { PrimaryFieldInputs } from './ScannerPage/components/FieldInputs';
import { EditableAdditionalFields } from './ScannerPage/components/AdditionalFields';
import ContactDetailView from './contact/ContactDetailView';
import DeleteConfirmationModal from './contact/DeleteConfirmationModal';
import useContactDetail from './contact/useContactDetail';

export default function ContactDetailPage() {
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const hook = useContactDetail();

  if (!hook) {
    return null;
  }

  const {
    contact,
    imageSrc,
    primaryFields,
    primaryFieldValues,
    additionalFields,
    isUpdating,
    updateError,
    handlePrimaryFieldChange,
    handleAdditionalFieldLabelChange,
    handleAdditionalFieldValueChange,
    handleAddAdditionalField,
    handleRemoveAdditionalField,
    handleConfirmInPlace,
    handleDelete,
  } = hook;

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    await handleDelete();
  };

  return (
    <div className="flex-1 flex flex-col items-center px-4 py-12">
      <div className="w-full max-w-5xl space-y-8">
        <div className="flex items-center justify-between">
          <Link
            to="/contacts"
            className="flex items-center gap-2 text-violet-300 hover:text-violet-200 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Contacts</span>
          </Link>
        </div>

        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Contact Details
          </h1>
          <p className="text-gray-300">
            {isEditMode
              ? 'Edit and update contact information'
              : 'View contact information'}
          </p>
        </div>
        {updateError && (
          <div className="bg-amber-400/90 text-gray-900 border border-amber-200 rounded-2xl px-4 py-3 text-sm shadow-lg">
            {updateError}
          </div>
        )}

        {!isEditMode ? (
          <ContactDetailView
            contact={contact}
            onEdit={() => setIsEditMode(true)}
            onDelete={() => setShowDeleteModal(true)}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <CardPreview imageSrc={imageSrc} />
              <div className="space-y-4">
                <PrimaryFieldInputs
                  fields={primaryFields}
                  values={primaryFieldValues}
                  onFieldChange={handlePrimaryFieldChange}
                />
                <EditableAdditionalFields
                  additionalFields={additionalFields}
                  onLabelChange={handleAdditionalFieldLabelChange}
                  onValueChange={handleAdditionalFieldValueChange}
                  onAddField={handleAddAdditionalField}
                  onRemoveField={handleRemoveAdditionalField}
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-white/10">
              <button
                type="button"
                onClick={() => {
                  setIsEditMode(false);
                }}
                className="px-6 py-3 rounded-2xl font-semibold text-white bg-white/10 hover:bg-white/20 transition-colors border border-white/15"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const success = await handleConfirmInPlace();
                  if (success) {
                    setIsEditMode(false);
                  }
                }}
                className="px-6 py-3 rounded-2xl font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg"
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        contactName={contact.full_name}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

